import { forwardRef, useImperativeHandle } from "react";
import { ClockIcon, LockIcon } from "lucide-react-native";

import {
  VnAlertDialog,
  VnAlertDialogContent,
  VnAlertDialogDescription,
  VnAlertDialogHeader,
  VnAlertDialogTitle,
} from "@/components/VnAlertDialog";
import { VnBox } from "@/components/VnBox";
import { VnButton } from "@/components/VnButton/VnButton";
import { VnIcon } from "@/components/VnIcon";
import { VnText } from "@/components/VnText/VnText";
import { useIdleTimer } from "@/hooks/useIdleTimer";

type SessionTimeoutDialogPropsType = {
  onTimeout: () => void;
  onManualLogout?: () => void;
};

export type SessionTimeoutDialogHandleType = {
  onUserActivity: () => void;
};

const formatCountdownTime = (totalSeconds: number): string => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

export const SessionTimeoutDialog = forwardRef<
  SessionTimeoutDialogHandleType,
  SessionTimeoutDialogPropsType
>(({ onTimeout, onManualLogout }, ref) => {
  const {
    isWarningVisible,
    remainingSeconds,
    onStayLoggedIn,
    onLogoutNow,
    onUserActivity,
  } = useIdleTimer({ onTimeout, onManualLogout });

  useImperativeHandle(ref, () => ({ onUserActivity }), [onUserActivity]);

  return (
    <VnAlertDialog open={isWarningVisible}>
      <VnAlertDialogContent>
        <VnAlertDialogHeader className="items-center">
          <VnBox className="bg-sidebar/10 mb-2 flex size-12 items-center justify-center rounded-full">
            <VnIcon as={ClockIcon} className="text-sidebar" size={24} />
          </VnBox>
          <VnAlertDialogTitle
            className="text-center"
            i18nKey="native:sessionTimeout.title"
          />
          <VnText
            variant="headerThree"
            className="text-sidebar text-center"
            i18nKey="native:sessionTimeout.countdown"
            i18nOptions={{ time: formatCountdownTime(remainingSeconds) }}
          />
          <VnAlertDialogDescription
            className="text-center"
            i18nKey="native:sessionTimeout.description"
          />
        </VnAlertDialogHeader>
        <VnBox className="flex flex-col gap-2">
          <VnButton
            className="w-full"
            onPress={onStayLoggedIn}
            i18nKey="native:sessionTimeout.stayLoggedIn"
          />
          <VnButton
            variant="outline"
            className="w-full"
            onPress={onLogoutNow}
            i18nKey="native:sessionTimeout.logOutNow"
          />
        </VnBox>
        <VnBox className="border-border flex flex-row items-center justify-center gap-1.5 border-t pt-4">
          <VnIcon as={LockIcon} className="text-muted-foreground" size={14} />
          <VnText
            variant="sm"
            className="text-muted-foreground"
            i18nKey="native:sessionTimeout.secureSession"
          />
        </VnBox>
      </VnAlertDialogContent>
    </VnAlertDialog>
  );
});

SessionTimeoutDialog.displayName = "SessionTimeoutDialog";
